import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { supabaseLocationService } from '../../services/supabaseLocationService';

export default async function fetchEducationalAttainmentByRaceSupabase(location_name) {
  try {
    
    // Initialize location service to get county information
    await supabaseLocationService.initialize();

    // Build targeted database query - only download what we need
    
    // Build separate queries for each location (like the working HTML version)
    let localQuery = supabaseClient
      .from('educational_attain_race')
      .select('*')
      .eq('location_name', location_name)
      .eq('educ_level', "Bachelor\\'s degree or higher")
      .gte('year', 2010)
      .lte('year', 2023)
      .order('year', { ascending: true });
    
    // Add California query for comparison
    const californiaQuery = supabaseClient
      .from('educational_attain_race')
      .select('*')
      .eq('location_name', 'California')
      .eq('educ_level', "Bachelor\\'s degree or higher")
      .gte('year', 2010)
      .lte('year', 2023)
      .order('year', { ascending: true });
    
    // Execute queries
    const { data: localQueryData, error: localError } = await localQuery;
    const { data: californiaQueryData, error: californiaError } = await californiaQuery;
    
    if (localError) throw localError;
    if (californiaError) { /* non-fatal */ }
    
    // Combine local and California data
    const data = [...(localQueryData || []), ...(californiaQueryData || [])];
    
    
    // If no data found, try a broader query to see what's available
    if (data.length === 0) {
      try {
        await supabaseClient
          .from('educational_attain_race')
          .select('*')
          .limit(10);
      } catch (broaderError) {
      }
    }
    
    // Show the actual filtered data structure
    
    
    // Show complete sample rows

    // Separate local and California data
    const localData = data.filter(d => 
      d.location_name && 
      d.location_name.toString().toLowerCase().includes(location_name.toLowerCase())
    );
    const californiaData = data.filter(d => 
      d.location_name && 
      d.location_name.toString().toLowerCase().includes('california')
    );
    
    // If no local data, try to find similar data or use available data
    if (localData.length === 0) {
      
      // Look for any city data that might be representative
      const availableCities = data.filter(row => 
        row.location_name && 
        row.location_name !== 'California' &&
        row.location_name !== location_name &&
        row.gender === 'Total' // Use total gender data
      );
      
      if (availableCities.length > 0) {
        // Use the first available city as a fallback
        const fallbackCity = availableCities[0];
        
        // Use the fallback city data (already filtered at database level)
        localData.push(...data.filter(row => 
          row.location_name === fallbackCity.location_name &&
          row.educ_level === "Bachelor\\'s degree or higher"
        ));
      } else {
        throw new Error('No educational attainment data available for any cities');
      }
    }
    
    // Get the most recent year available
    const years = [...new Set(data.map(row => row.year))].sort((a, b) => b - a);
    const mostRecentYear = years[0];
    
    // Data is already filtered at database level for Bachelor's degree
    // We need to aggregate Male/Female data to get total values
    const recentLocalData = localData.filter(d => d.year === mostRecentYear);
    const recentCaliforniaData = californiaData.filter(d => d.year === mostRecentYear);
    
    // Aggregate data by race (combine Male/Female records)
    const aggregateData = (data) => {
      const aggregated = {};
      data.forEach(row => {
        const key = `${row.race}_${row.year}`;
        if (!aggregated[key]) {
          aggregated[key] = {
            race: row.race,
            year: row.year,
            percent: 0,
            count: 0,
            records: []
          };
        }
        // Store individual records for debugging
        aggregated[key].records.push(row);
        // Sum the counts but average the percentages
        aggregated[key].count += (row.count || 0);
      });
      
      // Calculate proper percentages - use the "Total" gender record if available, otherwise use Female
      Object.values(aggregated).forEach(raceData => {
        const totalRecord = raceData.records.find(r => r.gender === 'Total');
        if (totalRecord) {
          // Use the Total record's percentage (convert from decimal to percentage)
          raceData.percent = (totalRecord.percent || 0) * 100;
        } else {
          // Use Female record's percentage (convert from decimal to percentage)
          const femaleRecord = raceData.records.find(r => r.gender === 'Female');
          raceData.percent = femaleRecord ? (femaleRecord.percent || 0) * 100 : 0;
        }
      });
      
      return Object.values(aggregated);
    };
    
    const aggregatedLocalData = aggregateData(recentLocalData);
    const aggregatedCaliforniaData = aggregateData(recentCaliforniaData);
    
    // Get unique races for the chart
    const races = [...new Set(aggregatedLocalData.map(row => row.race))].filter(race => race && race !== '');

    // Sort races by local data percentages (highest to lowest)
    const sortedRaces = races.sort((a, b) => {
      const aData = aggregatedLocalData.find(r => r.race === a);
      const bData = aggregatedLocalData.find(r => r.race === b);
      const aPercent = aData?.percent || 0;
      const bPercent = bData?.percent || 0;
      return bPercent - aPercent; // Sort highest to lowest
    });

    // Build datasets for the chart
    const datasets = [];
    
    // Local location data
    if (aggregatedLocalData.length > 0) {
      const localDataset = {
        label: location_name,
        data: sortedRaces.map(race => {
          const row = aggregatedLocalData.find(r => r.race === race);
          return row?.percent || 0;
        }),
        backgroundColor: '#3b82f6',
        borderColor: '#1e40af',
        borderWidth: 1
      };
      datasets.push(localDataset);
    }
    
    // California data for comparison
    if (aggregatedCaliforniaData.length > 0) {
      const californiaDataset = {
        label: 'California',
        data: sortedRaces.map(race => {
          const row = aggregatedCaliforniaData.find(r => r.race === race);
          return row?.percent || 0;
        }),
        backgroundColor: '#10b981',
        borderColor: '#059669',
        borderWidth: 1
      };
      datasets.push(californiaDataset);
    }

    
    return {
      chartData: {
        labels: sortedRaces,
        datasets: datasets
      },
      chartOptions: {
        type: 'bar',
        indexAxis: 'y', // This makes it a horizontal bar chart
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 8, right: 8, bottom: 40, left: 8 }
        },
        plugins: {
          legend: {
            ...standardLegend,
            display: true,
            position: 'bottom',
            align: 'center',
            fullSize: true
          },
          title: standardTitle((() => {
            // Check if we're using fallback data
            const isUsingFallback = localData.some(row => 
              row.location_name !== location_name
            );
            
            if (isUsingFallback) {
              const fallbackCity = localData.find(row => 
                row.location_name !== location_name
              )?.location_name || 'Representative City';
              return `Educational Attainment by Race - ${location_name} (Data from ${fallbackCity}) vs California (${mostRecentYear})`;
            } else {
              return `Educational Attainment by Race - ${location_name} vs California (${mostRecentYear})`;
            }
          })()),
          tooltip: {
            callbacks: {
              label: context => `${context.dataset.label}: ${typeof context.parsed.x === 'number' ? context.parsed.x.toFixed(1) : 'N/A'}%`
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: '% with Bachelor\'s Degree or Higher'
            },
            ticks: {
              callback: value => `${Number(value).toFixed(1)}%`
            },
            beginAtZero: true
          },
          y: {
            title: {
              display: true,
              text: 'Race/Ethnicity'
            }
          }
        }
      }
    };

  } catch (err) {
    console.error('Failed to generate educational attainment chart:', err);
    throw err;
  }
}
